import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, EyeOff, Loader2, TrendingDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Hint } from '@/components/Hint';
import { StatusPill } from '@/components/Status';
import { statusInk, type StatusTone } from '@/lib/status';
import { useAuth } from '@/auth/useAuth';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  getPriceChecks,
  keepPriceChecks,
  restorePriceChecks,
  updateProductPrices,
  type PriceCheckRow,
} from '@/api/orders';
import { ProductCell } from './ProductLink';
import { money, unitCost } from './lib';
import {
  RowAction, WorkEmpty, WorkLoading, WorkNotice, WorkSection, WorkSegmented, WorkTable, WorkToolbar,
} from './workLib';

type View = 'items' | 'receiving' | 'suspect' | 'kept';

const KEEP_HINT = 'Keep this markup; hides the product until it is received again.';

/** Well under the threshold is wrong, just under needs a look; the markup
 *  figure carries the tone. */
function markupTone(markup: number, threshold: number): StatusTone | null {
  if (markup < threshold * 0.85) return 'danger';
  if (markup < threshold) return 'warning';
  return null;
}

/**
 * Keystone on the landed cost, taken up to the next price ending in 9 —
 * 2.34 → 2.39, 4.98 → 4.99 — which is the ladder the catalogue already prices
 * on. Rounding UP is deliberate: the snap should never land the product below
 * the ×2 it was suggested to clear.
 */
function suggestPrice(cost: number): number {
  const c = Math.ceil(Number((cost * 200).toFixed(4)));
  return (c + ((9 - (c % 10)) + 10) % 10) / 100;
}

/** How the pack correction is described in one badge. */
function packNote(row: PriceCheckRow): { label: string; hint: string } | null {
  if (row.invoicedCost == null || row.qtyInvoiced == null) return null;
  const spread = `Invoiced ${row.qtyInvoiced} at ${unitCost(row.invoicedCost)}, received ${row.qtyReceived}: ${unitCost(row.cost)} each.`;
  return { label: row.packFactor ? `pack ×${row.packFactor}` : 'spread', hint: spread };
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

interface Pricing {
  /** pid → the price in the input, when it has been touched. */
  draft: Record<number, string>;
  setDraft: (pid: number, value: string) => void;
  /** pid → the pre-loaded suggestion. Absent for suspect rows. */
  suggestion: Map<number, number>;
  selected: Set<number>;
  toggle: (pid: number, on: boolean) => void;
  busy: Set<number>;
  errors: Map<number, string>;
  update: (pids: number[]) => void;
  /** "Keep as is" — hide until received again. */
  keep: (pids: number[]) => void;
  /** Undo a keep — the product returns to the pricing lists. */
  restore: (pids: number[]) => void;
  /** Products with no moving landed cost on any receiving — the ones safe to
   *  bulk-price. Select-all is limited to these. */
  costFinalPids: Set<number>;
}

function priceOf(pid: number, p: Pricing): string {
  return p.draft[pid] ?? (p.suggestion.has(pid) ? p.suggestion.get(pid)!.toFixed(2) : '');
}

function PriceTable({ rows, threshold, showReceiving, pricing, suspect, kept, readOnly }: {
  rows: PriceCheckRow[];
  threshold: number;
  showReceiving: boolean;
  pricing: Pricing;
  suspect?: boolean;
  /** Rendering the "Kept" list: row action is Restore, select-all is everything. */
  kept?: boolean;
  /** No orders:write:prices: no selection, no price input, no row actions — the table reads. */
  readOnly?: boolean;
}) {
  // Select-all covers only what could actually be submitted — a real price, and a
  // landed cost that has stopped moving. Picking up provisional rows here is the
  // one way this page could quietly cause a whole second repricing pass; a single
  // row is still yours to check and update deliberately. The kept list has no
  // such hazard — restore is always safe — so there select-all takes everything.
  const allPids = [...new Set(rows.map((r) => r.pid).filter((p): p is number => p != null))];
  const pids = kept
    ? allPids
    : allPids.filter((p) => Number.parseFloat(priceOf(p, pricing)) > 0 && pricing.costFinalPids.has(p));
  const allSelected = pids.length > 0 && pids.every((p) => pricing.selected.has(p));
  const selectAllHint = kept
    ? `Select all ${pids.length} kept product${pids.length === 1 ? '' : 's'}`
    : pids.length === 0
      ? 'Nothing selectable here; pick rows individually.'
      : `Select the ${pids.length} settled product${pids.length === 1 ? '' : 's'}`;

  return (
    <WorkTable
      cols={[
        ...(readOnly ? [] : ['w-10']),
        undefined, ...(showReceiving ? ['w-32'] : []), 'w-12', 'w-24', 'w-20', 'w-16',
        ...(readOnly ? [] : ['w-24', 'w-40']),
      ]}
      minWidth="960px"
      head={
        <>
          {!readOnly && (
            <th>
              <Hint content={selectAllHint}>
                <Checkbox
                  checked={allSelected}
                  disabled={pids.length === 0}
                  onCheckedChange={(on) => pids.forEach((p) => pricing.toggle(p, on === true))}
                  aria-label={selectAllHint}
                />
              </Hint>
            </th>
          )}
          <th>Item</th>
          {showReceiving && <th>Receiving</th>}
          <th className="num">Qty</th>
          <th className="num">
            <Hint content="What we paid per unit received, freight and fees included.">
              Landed cost
            </Hint>
          </th>
          <th className="num">
            <Hint content="Current selling price.">Sell price</Hint>
          </th>
          <th className="num">
            <Hint content="Sell price ÷ landed cost. Keystone is ×2.">Markup</Hint>
          </th>
          {!readOnly && (
            <>
              <th className="num">
                <Hint content={suspect ? 'Type a price to set.' : '2× landed cost, up to the next price ending in 9. Editable.'}>
                  New price
                </Hint>
              </th>
              <th />
            </>
          )}
        </>
      }
    >
      {rows.map((row) => {
        const pack = packNote(row);
        const pid = row.pid;
        const value = pid == null ? '' : priceOf(pid, pricing);
        const parsed = Number.parseFloat(value);
        const valid = Number.isFinite(parsed) && parsed > 0;
        const busy = pid != null && pricing.busy.has(pid);
        const failure = pid == null ? undefined : pricing.errors.get(pid);
        const cut = valid && parsed < row.sellingPrice;
        // Priced below landed cost is wrong outright; otherwise the markup says.
        const tone = suspect ? 'danger' : markupTone(row.markup, threshold);

        // No row wash: every row here is flagged, so a toned row would only
        // repeat the list's premise. The markup figure carries each row's state.
        return (
          <tr key={`${row.receiptId}:${row.pid ?? row.itemnumber ?? row.desc}`}>
            {!readOnly && (
              <td>
                {/* No price required to select: a row can be selected purely to
                    bulk-KEEP it. The bulk Update button counts (and submits)
                    only the selected rows that actually carry a valid price. */}
                <Checkbox
                  className="mt-0.5"
                  checked={pid != null && pricing.selected.has(pid)}
                  disabled={pid == null}
                  onCheckedChange={(on) => pid != null && pricing.toggle(pid, on === true)}
                  aria-label={`Select ${row.desc ?? 'product'}`}
                />
              </td>
            )}
            <td>
              <ProductCell desc={row.desc} pid={row.pid} itemnumber={row.itemnumber} vendorSku={row.vendorSku}>
                {failure && <div className={cn('mt-0.5 text-xs', statusInk('danger'))}>{failure}</div>}
                {suspect && row.suspectReason && (
                  <div className={cn('mt-0.5 text-xs', statusInk('danger'))}>{row.suspectReason}</div>
                )}
              </ProductCell>
            </td>
            {showReceiving && (
              <td>
                <div className="truncate">{row.vendor}</div>
                <div className="text-xs text-muted-foreground">#{row.receivingId}</div>
              </td>
            )}
            <td className="num">{row.qtyReceived}</td>
            <td className="num">
              {/* Provisional costs are the majority, so they get a dotted rule
                  and a tooltip rather than a badge — a badge on two rows in
                  three is wallpaper, and stops being read. */}
              <Hint
                content={
                  <>
                    <div>
                      {row.costBasis === 'target'
                        ? 'Computed target (invoice × factor)'
                        : 'Booked cost on the balanced receiving'}
                    </div>
                    {row.costProvisionalReason && <div className="mt-1.5">{row.costProvisionalReason}</div>}
                  </>
                }
              >
                <span className={cn(!row.costFinal && 'cursor-help underline decoration-dotted decoration-muted-foreground/70 underline-offset-2')}>
                  {unitCost(row.cost)}
                </span>
              </Hint>
              {pack && (
                <div className="mt-0.5">
                  <Hint content={pack.hint}><StatusPill>{pack.label}</StatusPill></Hint>
                </div>
              )}
            </td>
            <td className="num">{money(row.sellingPrice)}</td>
            <td className={cn('num', tone ? cn('font-medium', statusInk(tone)) : 'text-muted-foreground')}>
              ×{row.markup.toFixed(2)}
            </td>
            {!readOnly && (<>
            <td>
              <Hint content={cut && `Lower than the current ${money(row.sellingPrice)}.`}>
                <Input
                  value={value}
                  disabled={pid == null || busy}
                  onChange={(e) => pid != null && pricing.setDraft(pid, e.target.value)}
                  className={cn('px-1.5 text-right text-xs tabular-nums', cut && statusInk('warning'))}
                  inputMode="decimal"
                  placeholder={pid == null ? 'no pid' : '0.00'}
                />
              </Hint>
            </td>
            <td className="num">
              {kept ? (
                <RowAction
                  busy={busy}
                  disabled={pid == null || busy}
                  onClick={() => pid != null && pricing.restore([pid])}
                >
                  Restore
                </RowAction>
              ) : (
                <div className="inline-flex flex-wrap items-center justify-end gap-1">
                  <RowAction
                    busy={busy}
                    disabled={pid == null || !valid || busy}
                    onClick={() => pid != null && pricing.update([pid])}
                  >
                    Update
                  </RowAction>
                  <Hint content={KEEP_HINT}>
                    <RowAction
                      variant="quiet"
                      className="w-8 px-0"
                      disabled={pid == null || busy}
                      onClick={() => pid != null && pricing.keep([pid])}
                      aria-label={`Keep ${row.desc ?? 'product'} as is`}
                      icon={<EyeOff className="h-3.5 w-3.5" />}
                    />
                  </Hint>
                </div>
              )}
            </td>
            </>)}
          </tr>
        );
      })}
    </WorkTable>
  );
}

// ---------------------------------------------------------------------------
// View
// ---------------------------------------------------------------------------

/**
 * Landed cost vs current selling price for everything received — "based on what
 * we actually paid, are we selling this for a price that doesn't line up?" — and
 * the place to fix it, writing msrp and current price straight to the catalogue.
 *
 * A QUEUE, worked in two passes. The default view is the actionable half:
 * flagged rows whose landed cost is settled, safe to price once. The
 * provisional half (cost can still move while its receiving balances) is real
 * future work, revealed by one click — pricing it now means pricing it twice.
 * Rows that price below landed cost are a *cost* list, not a pricing one —
 * nearly always a pack price captured as a unit price — and sit under
 * "Cost check" so they can't monopolise the worst-first sort.
 *
 * Fetches its own rows (GET /orders/price-checks) — they were 3.7MB of the
 * overview payload, paid on every page load for a view that wasn't open.
 */
export function PriceCheckView({ onChanged }: {
  onChanged: () => void;
}) {
  const [priceChecks, setPriceChecks] = useState<PriceCheckRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Every write here lands on the LIVE shop (POST /orders/price-updates and
  // the keep/restore pair) — orders:write:prices. Without it the page is the
  // review, read: rows, costs, markups, and no way to change a price.
  const canPrice = useAuth().can('orders:write:prices');
  const [thresholdInput, setThresholdInput] = useState('2.0');
  const [onlyFlagged, setOnlyFlagged] = useState(true);
  // ON by default: this is the queue's actionable half. The provisional rows
  // stay one click away behind the waiting line below the controls.
  const [settledOnly, setSettledOnly] = useState(true);
  const [view, setView] = useState<View>('items');

  const loadChecks = useCallback(async () => {
    try {
      const res = await getPriceChecks();
      setPriceChecks(res.priceChecks);
      setLoadError(null);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load price checks');
    }
  }, []);
  useEffect(() => { loadChecks(); }, [loadChecks]);

  /** Every write refreshes both this view's rows and the sidebar counts. */
  const refresh = useCallback(() => {
    loadChecks();
    onChanged();
  }, [loadChecks, onChanged]);

  // Rendering every flagged line at once (2,000+ rows) is what made this view
  // feel dead after the click — cap the table and grow on demand instead.
  const PAGE = 150;
  const [visible, setVisible] = useState(PAGE);

  const [draft, setDraftState] = useState<Record<number, string>>({});
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState<Set<number>>(new Set());
  const [errors, setErrors] = useState<Map<number, string>>(new Map());
  const [failure, setFailure] = useState<string | null>(null);
  /** The last bulk result: warning when any row failed, else success. */
  const [done, setDone] = useState<{ text: string; tone: StatusTone } | null>(null);

  const threshold = Number.parseFloat(thresholdInput) || 2.0;

  // Kept ("keep as is") rows leave BOTH working lists — they are decided — and
  // live under their own tab until a new receiving of the product resurfaces
  // them server-side.
  const rowsAll = useMemo(() => priceChecks ?? [], [priceChecks]);
  const priced = useMemo(() => rowsAll.filter((r) => !r.suspect && !r.dismissed), [rowsAll]);
  const suspects = useMemo(
    () => [...rowsAll.filter((r) => r.suspect && !r.dismissed)].sort((a, b) => a.markup - b.markup),
    [rowsAll],
  );
  const keptRows = useMemo(
    () => [...rowsAll.filter((r) => r.dismissed)].sort((a, b) => a.markup - b.markup),
    [rowsAll],
  );

  const rows = useMemo(() => {
    let filtered = onlyFlagged ? priced.filter((r) => r.markup < threshold) : priced;
    if (settledOnly) filtered = filtered.filter((r) => r.costFinal);
    return [...filtered].sort((a, b) => a.markup - b.markup);
  }, [priced, onlyFlagged, threshold, settledOnly]);

  // A product is only safe to bulk-price when NO receiving it appeared on still
  // has a moving cost. `every`, not `some`: the dearest of those costs is what
  // seeds the suggestion, and that could be the one about to change.
  const costFinalPids = useMemo(() => {
    const ok = new Map<number, boolean>();
    for (const r of priced) {
      if (r.pid == null) continue;
      ok.set(r.pid, (ok.get(r.pid) ?? true) && r.costFinal);
    }
    return new Set([...ok].filter(([, settled]) => settled).map(([pid]) => pid));
  }, [priced]);

  // One price per product, even though a product recurs across receivings. When
  // its landed cost differs between them the dearest wins, so the suggestion is
  // never below keystone on any receiving it appeared in.
  const suggestion = useMemo(() => {
    const m = new Map<number, number>();
    for (const r of priced) {
      if (r.pid == null) continue;
      const s = suggestPrice(r.cost);
      if (!m.has(r.pid) || s > m.get(r.pid)!) m.set(r.pid, s);
    }
    return m;
  }, [priced]);

  const byReceiving = useMemo(() => {
    const map = new Map<string, { first: PriceCheckRow; rows: PriceCheckRow[] }>();
    for (const row of rows) {
      const entry = map.get(row.receiptId) || { first: row, rows: [] };
      entry.rows.push(row);
      map.set(row.receiptId, entry);
    }
    return [...map.values()].sort((a, b) => b.first.receivingId - a.first.receivingId);
  }, [rows]);

  const flaggedCount = priced.filter((r) => r.markup < threshold).length;
  // Settled WITHIN the flagged set — "2032 settled" against "2016 flagged" read
  // as nonsense when the settled count spanned all priced lines.
  const flaggedSettledCount = priced.filter((r) => r.markup < threshold && r.costFinal).length;

  const setDraft = (pid: number, value: string) => {
    setDraftState((d) => ({ ...d, [pid]: value }));
    setErrors((e) => { const next = new Map(e); next.delete(pid); return next; });
  };

  const toggle = (pid: number, on: boolean) => {
    setSelected((s) => {
      const next = new Set(s);
      if (on) next.add(pid); else next.delete(pid);
      return next;
    });
  };

  const update = async (pids: number[]) => {
    const updates = pids
      .map((pid) => ({ pid, price: Number.parseFloat(draft[pid] ?? String(suggestion.get(pid) ?? '')) }))
      .filter((u) => Number.isFinite(u.price) && u.price > 0);
    if (!updates.length) return;

    setBusy((b) => { const next = new Set(b); updates.forEach((u) => next.add(u.pid)); return next; });
    setFailure(null);
    setDone(null);
    try {
      const res = await updateProductPrices(updates);

      // Functional updates throughout: a row edited while the request was in
      // flight must not be reverted by state captured before it was sent.
      setErrors((prev) => {
        const next = new Map(prev);
        for (const r of res.results) {
          if (r.ok) next.delete(r.pid);
          else next.set(r.pid, r.error || 'The catalog rejected this price.');
        }
        return next;
      });
      setSelected((prev) => {
        const next = new Set(prev);
        for (const r of res.results) if (r.ok) next.delete(r.pid);
        return next;
      });
      // Drop the draft on success so the row re-derives from the refreshed data.
      setDraftState((prev) => {
        const next = { ...prev };
        for (const r of res.results) if (r.ok) delete next[r.pid];
        return next;
      });
      setDone(res.failed
        ? { text: `${res.updated} price${res.updated === 1 ? '' : 's'} updated, ${res.failed} failed.`, tone: 'warning' }
        : { text: `${res.updated} price${res.updated === 1 ? '' : 's'} updated.`, tone: 'success' });

      if (res.updated > 0) refresh();
    } catch (e) {
      setFailure(e instanceof Error ? e.message : 'Failed to update prices');
    } finally {
      setBusy((b) => { const next = new Set(b); updates.forEach((u) => next.delete(u.pid)); return next; });
    }
  };

  /**
   * Record (or undo) "keep as is" for a set of products, then reload the
   * overview — the rows move between the working lists and the Kept tab on the
   * server's word, not an optimistic local copy.
   */
  const setKept = async (pids: number[], restore: boolean) => {
    if (!pids.length) return;
    setBusy((b) => { const next = new Set(b); pids.forEach((p) => next.add(p)); return next; });
    setFailure(null);
    setDone(null);
    try {
      if (restore) await restorePriceChecks(pids);
      else await keepPriceChecks(pids);
      setSelected((prev) => {
        const next = new Set(prev);
        pids.forEach((p) => next.delete(p));
        return next;
      });
      setDone({ text: `${pids.length} product${pids.length === 1 ? '' : 's'} ${restore ? 'restored to the pricing lists' : 'kept as is'}.`, tone: 'success' });
      refresh();
    } catch (e) {
      setFailure(e instanceof Error ? e.message : `Failed to ${restore ? 'restore' : 'keep'} products`);
    } finally {
      setBusy((b) => { const next = new Set(b); pids.forEach((p) => next.delete(p)); return next; });
    }
  };

  // Any change to what the list IS starts the cap over.
  useEffect(() => {
    setVisible(PAGE);
  }, [view, threshold, onlyFlagged, settledOnly]);

  // A selection is an intent, and the intent differs per tab — rows picked to
  // reprice must not survive into the Kept tab as rows about to be restored.
  useEffect(() => {
    setSelected(new Set());
  }, [view]);

  const pricing: Pricing = {
    draft, setDraft, suggestion, selected, toggle, busy, errors, update, costFinalPids,
    keep: (pids) => setKept(pids, false),
    restore: (pids) => setKept(pids, true),
  };
  const bulkBusy = [...selected].some((p) => busy.has(p));
  const provisionalSelected = [...selected].filter((p) => !costFinalPids.has(p)).length;
  // Selection no longer requires a valid price (keeping doesn't need one), so
  // the bulk Update button reports how many rows it would actually submit.
  const priceableSelected = [...selected].filter((p) => {
    const v = Number.parseFloat(draft[p] ?? (suggestion.has(p) ? suggestion.get(p)!.toFixed(2) : ''));
    return Number.isFinite(v) && v > 0;
  }).length;

  if (loadError) {
    return (
      <WorkNotice tone="danger" onRetry={() => void loadChecks()}>
        Price checks failed to load: {loadError}
      </WorkNotice>
    );
  }
  if (priceChecks === null) {
    return <WorkLoading>Loading price checks…</WorkLoading>;
  }
  if (priceChecks.length === 0) {
    return (
      <WorkEmpty icon={<TrendingDown />} detail="Selling prices populate as receivings are re-mirrored.">
        No selling-price data yet.
      </WorkEmpty>
    );
  }

  return (
    <div>
      <WorkToolbar>
        <WorkSegmented<View>
          label="Price review list"
          value={view}
          onChange={setView}
          options={[
            { value: 'items', label: 'All items' },
            { value: 'receiving', label: 'By receiving' },
            { value: 'suspect', label: `Cost check${suspects.length ? ` (${suspects.length})` : ''}` },
            { value: 'kept', label: `Kept${keptRows.length ? ` (${keptRows.length})` : ''}` },
          ]}
        />

        {(view === 'items' || view === 'receiving') && (
          <>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              Flag below ×
              <Input
                value={thresholdInput}
                onChange={(e) => setThresholdInput(e.target.value)}
                aria-label="Flag below markup"
                className="w-16 px-1.5 text-xs tabular-nums"
                inputMode="decimal"
              />
            </label>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Switch checked={onlyFlagged} onCheckedChange={setOnlyFlagged} />
              Only flagged
            </label>
            <Hint content="Only products whose landed cost can no longer move.">
              <label className="flex cursor-help items-center gap-1.5 text-xs text-muted-foreground">
                <Switch checked={settledOnly} onCheckedChange={setSettledOnly} />
                Settled cost only
              </label>
            </Hint>
            <span className="ml-auto text-xs text-muted-foreground">
              {flaggedCount} of {priced.length} priced products below ×{threshold.toFixed(2)}
              {' · '}
              <Hint content="Flagged products whose landed cost is settled.">
                <span>{flaggedSettledCount} of them settled</span>
              </Hint>
            </span>
          </>
        )}
      </WorkToolbar>

      {/* The queue's other half, named — so hiding it never reads as done. */}
      {(view === 'items' || view === 'receiving') && settledOnly && flaggedCount > flaggedSettledCount && (
        <WorkNotice tone="neutral">
          {flaggedCount - flaggedSettledCount} more flagged product{flaggedCount - flaggedSettledCount === 1 ? '' : 's'} waiting on
          balancing (provisional landed cost) —{' '}
          <button
            type="button"
            className="text-primary hover:underline"
            onClick={() => setSettledOnly(false)}
          >
            show them
          </button>
        </WorkNotice>
      )}

      {/* Bulk bar — sticky so a long selection stays actionable. Near-opaque
          background (with a flat primary tint painted over it): it floats over
          table rows while scrolling, and at bg-primary/5 the rows bled through. */}
      {selected.size > 0 && (
        <div className="sticky top-[var(--work-toolbar-h,0px)] z-10 my-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-primary/30 bg-[color-mix(in_srgb,var(--primary)_8%,var(--card))] px-3 py-2 text-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.06)]">
          <span className="font-medium">
            {selected.size} product{selected.size === 1 ? '' : 's'} selected
          </span>
          {view !== 'kept' && provisionalSelected > 0 && (
            <Hint content="A landed cost that can still move; expect to reprice once the receiving settles.">
              <span className={cn('flex items-center gap-1', statusInk('warning'))}>
                <AlertTriangle className="h-3 w-3" />
                {provisionalSelected} provisional
              </span>
            </Hint>
          )}
          <div className="ml-auto flex items-center gap-2">
            <RowAction variant="quiet" onClick={() => setSelected(new Set())} disabled={bulkBusy}>
              Clear
            </RowAction>
            {view === 'kept' ? (
              <Button size="sm" className="text-xs" onClick={() => setKept([...selected], true)} disabled={bulkBusy}>
                {bulkBusy && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                Restore {selected.size} product{selected.size === 1 ? '' : 's'}
              </Button>
            ) : (
              <>
                <Hint content={KEEP_HINT}>
                  <RowAction
                    icon={<EyeOff className="h-3 w-3" />}
                    onClick={() => setKept([...selected], false)}
                    disabled={bulkBusy}
                  >
                    Keep as is
                  </RowAction>
                </Hint>
                <Button size="sm" className="text-xs" onClick={() => update([...selected])} disabled={bulkBusy || priceableSelected === 0}>
                  {bulkBusy && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                  Update {priceableSelected} price{priceableSelected === 1 ? '' : 's'}
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {failure && <WorkNotice tone="danger">{failure}</WorkNotice>}
      {done && !failure && <WorkNotice tone={done.tone}>{done.text}</WorkNotice>}

      {view === 'kept' ? (
        keptRows.length === 0 ? (
          <WorkEmpty compact icon={<EyeOff />}>Nothing kept.</WorkEmpty>
        ) : (
          <div className="space-y-2">
            <PriceTable readOnly={!canPrice} rows={keptRows.slice(0, visible)} threshold={threshold} showReceiving pricing={pricing} kept />
            <ShowMore total={keptRows.length} visible={visible} onMore={() => setVisible((v) => v + 500)} />
          </div>
        )
      ) : view === 'suspect' ? (
        suspects.length === 0 ? (
          <WorkEmpty compact>No product priced below its landed cost.</WorkEmpty>
        ) : (
          <div className="space-y-2">
            <WorkNotice tone="warning">
              Priced below landed cost, usually a pack price captured as a unit price. Re-read the invoice before repricing.
            </WorkNotice>
            <PriceTable readOnly={!canPrice} rows={suspects.slice(0, visible)} threshold={threshold} showReceiving pricing={pricing} suspect />
            <ShowMore total={suspects.length} visible={visible} onMore={() => setVisible((v) => v + 500)} />
          </div>
        )
      ) : rows.length === 0 ? (
        <WorkEmpty compact>
          {settledOnly && flaggedCount > 0
            ? 'Nothing safe to price right now — every flagged product is still waiting on its balance.'
            : <>Every priced product clears ×{threshold.toFixed(2)}.</>}
        </WorkEmpty>
      ) : view === 'items' ? (
        <>
          <PriceTable readOnly={!canPrice} rows={rows.slice(0, visible)} threshold={threshold} showReceiving pricing={pricing} />
          <ShowMore total={rows.length} visible={visible} onMore={() => setVisible((v) => v + 500)} />
        </>
      ) : (
        <div>
          {(() => {
            // Same cap, group-shaped: render whole receivings until the row
            // budget is spent, so groups never render half a table.
            const shown: typeof byReceiving = [];
            let budget = visible;
            for (const g of byReceiving) {
              if (budget <= 0) break;
              shown.push(g);
              budget -= g.rows.length;
            }
            return (
              <>
                {shown.map(({ first, rows: group }) => (
                  <WorkSection
                    key={first.receiptId}
                    title={
                      <>
                        <span>{first.vendor}</span>
                        <span className="text-xs font-normal text-muted-foreground">#{first.receivingId}</span>
                      </>
                    }
                    count={group.length}
                  >
                    <PriceTable readOnly={!canPrice} rows={group} threshold={threshold} showReceiving={false} pricing={pricing} />
                  </WorkSection>
                ))}
                <ShowMore
                  total={byReceiving.reduce((a, g) => a + g.rows.length, 0)}
                  visible={shown.reduce((a, g) => a + g.rows.length, 0)}
                  onMore={() => setVisible((v) => v + 500)}
                />
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}

function ShowMore({ total, visible, onMore }: { total: number; visible: number; onMore: () => void }) {
  if (total <= visible) return null;
  return (
    <div className="flex justify-center py-3">
      <RowAction onClick={onMore}>Show more ({total - visible} remaining)</RowAction>
    </div>
  );
}
